import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GridItem from '../../../layout/GridComponents/GridItem';
import { flexify } from '../../../../utilities/cssMixins';
import DataContainer from './historicalComponents/data-container/data-container.component';
import { createSpecificTitleBarSelectSelector } from '../../../../redux/pages/selectors';
import {
  useAdditionalTitleBarDatePicker,
  useAdditionalTitleBarSelect,
} from '../../../../hooks/useAdditionalTitleBarComponents';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import useTitleBarDatePicker from '../../../../hooks/useTitleBarDatePicker';
import { useRemoveTitleBarSelect } from '../../../../hooks/useRemoveTitleBarComponent';
import dayjs from 'dayjs';
import {
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../utilities/dateUtilities';
import { Grid, Typography } from '@mui/material';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import {
  useHistoricalDataQuery,
  useRiskReportDataKeysQuery,
} from '../../../../api/rs-hg/risk/hooks/queries';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { ValuesType } from './historicalComponents/controlBar/controlBar.component';
import type { HistoricalPageModel } from '../../../../api/rs-hg/risk/historicalData.types';

interface HistoricalDataPanelProps {
  initialTopLevel: ValuesType;
  syncTopLevel?: ValuesType;
  itemIdentifier: number;
  fourShowing: boolean;
  activeFund: string;
  fixedTitle?: 'Table' | 'Time Series';
  external: boolean;
}

const HistoricalDataPanel: FC<HistoricalDataPanelProps> = ({
  initialTopLevel,
  syncTopLevel,
  itemIdentifier,
  fourShowing,
  activeFund,
  fixedTitle,
  external,
}) => {
  const [topLevel, setTopLevel] = useState<ValuesType>(initialTopLevel);

  useEffect(() => {
    if (syncTopLevel) {
      setTopLevel(syncTopLevel);
      return;
    }

    setTopLevel(initialTopLevel);
  }, [initialTopLevel, syncTopLevel]);

  const historicalQuery = useHistoricalDataQuery({
    fundName: activeFund,
    date1: topLevel.fromDate ?? '',
    date2: topLevel.toDate ?? '',
    key1: topLevel.key1,
    key2: topLevel.key2,
  });

  return (
    <QueryBoundary<HistoricalPageModel>
      card
      isLoading={historicalQuery.isLoading}
      isFetching={historicalQuery.isFetching}
      isError={historicalQuery.isError}
      error={historicalQuery.error}
      data={
        historicalQuery.data?.data?.length ? historicalQuery.data : null
      }
      loadingMessages={['Loading historical data...']}
      errorMessage="Unable to load historical data."
      noDataMessage="No historical data available for the selected keys and dates."
    >
      {(pageModel) => (
        <DataContainer
          key={itemIdentifier + 'historicalDataContainer'}
          itemIdentifier={itemIdentifier}
          fourShowing={fourShowing}
          topLevel={topLevel}
          onTopLevelChange={fourShowing ? setTopLevel : undefined}
          activeFund={activeFund}
          data={pageModel.data}
          fixedTitle={fixedTitle}
          external={external}
        />
      )}
    </QueryBoundary>
  );
};

interface HistoricalSingleViewProps {
  topLevel: ValuesType;
  activeFund: string;
  fourShowing: boolean;
}

const HistoricalSingleView: FC<HistoricalSingleViewProps> = ({
  topLevel,
  activeFund,
  fourShowing,
}) => {
  const historicalQuery = useHistoricalDataQuery({
    fundName: activeFund,
    date1: topLevel.fromDate ?? '',
    date2: topLevel.toDate ?? '',
    key1: topLevel.key1,
    key2: topLevel.key2,
  });

  return (
    <QueryBoundary<HistoricalPageModel>
      isLoading={historicalQuery.isLoading}
      isFetching={historicalQuery.isFetching}
      isError={historicalQuery.isError}
      error={historicalQuery.error}
      data={
        historicalQuery.data?.data?.length ? historicalQuery.data : null
      }
      loadingMessages={['Loading historical data...']}
      errorMessage="Unable to load historical data."
      noDataMessage="No historical data available for the selected keys and dates."
    >
      {(pageModel) => (
        <>
          <DataContainer
            itemIdentifier={0}
            fourShowing={fourShowing}
            topLevel={topLevel}
            activeFund={activeFund}
            data={pageModel.data}
            fixedTitle="Time Series"
            external
          />
          <DataContainer
            itemIdentifier={1}
            fourShowing={fourShowing}
            topLevel={topLevel}
            activeFund={activeFund}
            data={pageModel.data}
            fixedTitle="Table"
            external
          />
        </>
      )}
    </QueryBoundary>
  );
};

const HistoricalData: FC<FundInfoComponentProps> = (props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'View: ',
        titleBarKey: 'viewConfigurationHistorical',
        values: [
          {
            label: 'One',
            value: 'single',
          },
          {
            label: 'Four',
            value: 'four',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'single',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent(
          'viewConfigurationHistorical',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [dispatch]);

  const startDateWeekday = getWeekdayXMonthsAgo(3);
  const endDateWeekday = getMostRecentWeekday();
  useAdditionalTitleBarDatePicker({
    displayOnOverviewPage: true,
    titleBarKey: 'startDate',
    titleBarTitle: 'Start Date: ',
    currentValue: dayjs(startDateWeekday).format('YYYY-MM-DD'),
  });
  useAdditionalTitleBarDatePicker({
    displayOnOverviewPage: true,
    titleBarKey: 'endDate',
    titleBarTitle: 'End Date: ',
    currentValue: dayjs(endDateWeekday).format('YYYY-MM-DD'),
  });

  const viewConfigurationSelector = createSpecificTitleBarSelectSelector(
    'viewConfigurationHistorical',
  );
  const viewConfiguration = useSelector(viewConfigurationSelector);
  const showFour = viewConfiguration[0]?.currentValue === 'four' || false;

  const { data: ucitsRiskReportDataKeys } = useRiskReportDataKeysQuery(
    props.fundId,
  );
  const availableKeys: ReadonlyArray<string> = ucitsRiskReportDataKeys || [];
  const hasKeys = availableKeys.length > 0;
  const defaultKey1 = hasKeys ? availableKeys[0] : 'none_selected';
  const defaultKey2 = hasKeys
    ? (availableKeys[1] ?? availableKeys[0])
    : 'none_selected';

  const keysSelectRerenderTrigger = showFour !== (availableKeys.length > 0);

  useAdditionalTitleBarSelect(
    {
      displayOnOverviewPage: true,
      titleBarKey: 'key1',
      titleBarTitle: 'Key 1: ',
      values: availableKeys.map((key: string) => {
        return {
          label: key
            .split('_')
            .map((word: string) => word[0].toUpperCase() + word.substring(1))
            .join(' '),
          value: key,
        };
      }),
      aValueIsAlwaysSelected: hasKeys,
      currentValue: defaultKey1,
    },
    true,
    keysSelectRerenderTrigger,
  );
  useAdditionalTitleBarSelect(
    {
      displayOnOverviewPage: true,
      titleBarKey: 'key2',
      titleBarTitle: 'Key 2: ',
      values: availableKeys.map((key: string) => {
        return {
          label: key
            .split('_')
            .map((word: string) => word[0].toUpperCase() + word.substring(1))
            .join(' '),
          value: key,
        };
      }),
      aValueIsAlwaysSelected: hasKeys,
      currentValue: defaultKey2,
    },
    true,
    keysSelectRerenderTrigger,
  );

  const key1 = useTitleBarSelect('key1') || defaultKey1;
  const key2 = useTitleBarSelect('key2') || defaultKey2;
  const fromDate = useTitleBarDatePicker('startDate');
  const toDate = useTitleBarDatePicker('endDate');

  const topLevel: ValuesType = {
    key1,
    key2,
    fromDate,
    toDate,
  };

  const fourChartsTopLevels: ValuesType[] = [
    topLevel,
    {
      key1: 'portfolio_asset_class_equity_gross_exposure',
      key2: 'portfolio_asset_class_equity_net_exposure',
      fromDate,
      toDate,
    },
    {
      key1: 'portfolio_asset_class_fixed_income_gross_exposure',
      key2: 'portfolio_asset_class_fixed_income_net_exposure',
      fromDate,
      toDate,
    },
    {
      key1: 'portfolio_asset_class_alternative_gross_exposure',
      key2: 'portfolio_asset_class_alternative_net_exposure',
      fromDate,
      toDate,
    },
  ];

  const selectKey1Value = useTitleBarSelect('key1');
  const removeKey1 = useRemoveTitleBarSelect('key1');
  const selectKey2Value = useTitleBarSelect('key2');
  const removeKey2 = useRemoveTitleBarSelect('key2');
  if (showFour && selectKey1Value !== null && selectKey2Value !== null) {
    removeKey1();
    removeKey2();
  }

  const ready =
    props.fundId !== 'none_selected' && key1 && key2 && fromDate && toDate;

  return (
    <Grid
      container
      spacing={2}
      style={{ margin: 0 }}
      id={showFour ? 'pdf-target' : 'grid'}
    >
      {ready ? (
        showFour ? (
          [0, 1, 2, 3].map((num) => (
            <HistoricalDataPanel
              key={num + 'historicalDataPanel'}
              itemIdentifier={num}
              fourShowing={showFour}
              initialTopLevel={fourChartsTopLevels[num]}
              syncTopLevel={num === 0 ? topLevel : undefined}
              activeFund={props.fundId}
              external={false}
            />
          ))
        ) : (
          <HistoricalSingleView
            topLevel={topLevel}
            activeFund={props.fundId}
            fourShowing={showFour}
          />
        )
      ) : (
        <GridItem
          xs={12}
          style={{
            height: 'calc(100vh - 13.75rem)',
            ...flexify('center', 'center'),
          }}
        >
          <Typography variant="h1">
            {props.fundId !== 'none_selected'
              ? 'Please use the keys and dates to query data'
              : 'Please select  a fund to begin'}
          </Typography>
        </GridItem>
      )}
    </Grid>
  );
};

export default HistoricalData;
